import { useContext, useEffect, useState } from "react";
import axios from "axios";
import NTree from "./NTree";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { api } from "../services/config";
import { SemanticTreeContext } from "./SemanticTree";
import { useOutletContext } from 'react-router-dom';
import { ProjectContext } from "../screens/ProjectScreen";
import TranslateIcon from '@mui/icons-material/Translate';

function NTreeItem({
    item: semantic,
}) {
    const [open, setOpen] = useState(false);
    const [item, setItem] = useState(semantic);
    const [refresh, setRefresh] = useState(false);

    const {
        setTermData, treeState, setTreeState,
        headers,
    } = useContext(ProjectContext);
    const { semanticTarget, semanticSource, item: item_id, updatedSemantics } = treeState;
    const {
        selectedSC, setSelectedSC,
        updatedSemantic,
        related,
        associated,
        keywordId,
        setAssociatedClicked,
        setSemanticClicked,
        splittedTree,
        hyponyms, resetSearch,
    } = useContext(SemanticTreeContext)

    const { profileState } = useOutletContext();
    const { projectRole: { projectId } } = profileState

    if (
        (semanticTarget || semanticSource)
        && (semanticTarget === item.id || semanticSource === item.id)
        // INFO: Second condition prevents rerender
        && (!refresh || `${semanticTarget}${semanticSource}${item_id}` !== refresh)
    ) {
        setRefresh(`${semanticTarget}${semanticSource}${item_id}`);
    }

    if (
        (updatedSemantics?.includes(item.id))
        && (!refresh || `${updatedSemantics}${item_id}` !== refresh)
    ) {
        setRefresh(`${updatedSemantics}${item_id}`);
    }

    useEffect(() => {
        async function fetchSC() {
            await axios
                .put(
                    `${api.project}${projectId}/sc/`,
                    { 'ids': [item.id] },
                    { headers }
                )
                .then((res) => {
                    setItem(res.data.classes?.[0])
                    setTreeState({})
                })
        }
        if (refresh) { fetchSC(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, refresh, item.id]);

    useEffect(() => {
        if (item.hyponym_classes.length > 0) setOpen(true)
    }, [item]);

    const onClickButton = () => {
        if (hyponyms && keywordId !== item.term.id) resetSearch();
        setOpen((open) => !open);
        // INFO: Prioritize tree expansion rather than term synchronization
        setTimeout(async () => {
            const term = await fetchTerm(item.term.id);
            fetchSC(item.id, term);
        }, 100);
    };

    const onClickTermRelation = (id) => {
        fetchTerm(id);
    };

    async function onClickAssociatedClass(item) {
        const { data } = await axios.get(`${api.project}${projectId}/terms/${item.term.id}`);
        setTermData(data)
        setAssociatedClicked && setAssociatedClicked(true);
    }

    async function fetchTerm(id) {
        const { data } = await axios.get(`${api.project}${projectId}/terms/${id}`);
        setTermData(data)
        setSemanticClicked(true);
        return data;
    }

    async function fetchSC(id, term) {
        await axios
            .put(
                `${api.project}${projectId}/sc/`,
                { ids: [id] },
                { headers }
            )
            .then((res) => {
                setSelectedSC(res.data.classes[0])
                updatedSemantic(res.data.classes[0], term)
            })
            .catch(function (error) {
                return Promise.reject(error);
            });
    }

    return (
        <div>
            <Droppable droppableId={"1" + item.id?.toString()} isDropDisabled={true}>
                {(provided) => (
                    <div>
                        <div className="droppable-box">
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                <Draggable
                                    key={item.id}
                                    draggableId={`{"id": ${item.id}, "type": "semantic", "hyper": ${item.hyper}, "splittedTree": ${splittedTree || false}}`}
                                    index={item.id}
                                >
                                    {(provided) => (
                                        <div
                                            onClick={onClickButton}
                                            style={{ cursor: "pointer" }}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            {(item.hyponyms.length > 0
                                                ? <i
                                                    className={`fas fa-caret-${open ? "down" : "right"} tab-w-1 tab-mr-2`}
                                                    role="button"
                                                />
                                                : (<i className={"fas fa-caret-right tab-w-1 tab-mr-2 text-muted-light"} />)
                                            )}
                                            <span
                                                className={
                                                    keywordId === item.term.id
                                                        ? `highlightSemantic`
                                                        : item.term.id === selectedSC?.term?.id
                                                            ? "highlightSemanticClicked"
                                                            : ""
                                                }
                                            >
                                                {item.term.reference}
                                            </span>
                                        </div>
                                    )}
                                </Draggable>
                            </div>
                        </div>
                    </div>
                )}
            </Droppable>
            <Droppable droppableId={`{"itemId": ${item.id}, "type": "subClasse", "splittedTree": ${splittedTree || false}}`}>
                {(provided, snapshot) => (
                    <div
                        className={`ml-3 droppable-box-mini ${snapshot.isDraggingOver ? "dragging" : ""
                            }`}
                        style={{
                            borderBottom: snapshot.isDraggingOver
                                ? "1px solid rgb(32, 82, 12)"
                                : "",
                        }}
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        id="box-mini"
                    ></div>
                )}
            </Droppable>

            {related && item.related?.length > 0 && (
                <div className="tab-ml-3 opacity-50">
                    {item.related.map((relation) => (
                        <div
                            style={{ cursor: "pointer", width: "fit-content" }}
                            onClick={() => onClickTermRelation(relation.id)}
                        >
                            {relation.relation === "VARIATE" && (
                                <b className="tab-relation d-inline-block text-center f-w-1">V</b>
                            )}
                            {relation.relation === "ACRONYM" && (
                                <b className="tab-relation d-inline-block text-center f-w-1">...</b>
                            )}
                            {relation.relation === "TRANSLT" && (
                                <TranslateIcon fontSize="small" className="tab-relation d-inline-block text-center" />
                            )}
                            {relation.relation === "SYNONYM" && (
                                <b className="tab-relation d-inline-block text-center f-w-1">=</b>
                            )}
                            {relation.relation === "QUA-SYN" && (
                                <b className="tab-relation d-inline-block text-center f-w-1">≈</b>
                            )}
                            <span
                                className={
                                    keywordId === relation.id
                                        ? `highlightSemantic`
                                        : ""
                                }
                            >
                                {relation.reference}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {associated &&
                item.associated.map((item) => (
                    <div
                        className="tab-ml-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => onClickAssociatedClass(item)}
                    >
                        <span className="tab-mr-2 tab-w-1 d-inline-block f-s-15">⊶</span>{item.term.reference}
                    </div>
                ))
            }

            {item.hyponym_classes.length > 0
                ? (
                    open && <NTree hyponyms={item.hyponym_classes} />
                )
                : (
                    open && item.hyponyms.length > 0 && <NTree id={item.id} />
                )
            }
        </div>
    );
}
export default NTreeItem;
